import { debounce } from "lodash-es";
import { attribute, float, Fn, Switch, uint } from "three/tsl";
import * as THREE from "three/webgpu";
import { GameEngine } from "~/engine/instance";
import { TerrainBackground } from "~/renderers/terrain-background";
import type { Vector2 } from "~/state/game-types";
import {
  type TerrainType,
  TERRAIN_TYPE_LIST,
  TERRAIN_TYPES_MAP,
  VOXEL_EMPTY,
} from "~/terrain/types";
import type { Voxel, VoxelChunk } from "~/terrain/voxel-terrain";
import { mapNumber } from "~/utils/math";
import { pseudoRandom } from "~/utils/random";
import {
  disposeMesh,
  tmpColor,
  tmpObject,
  tmpVector3,
  ZEROED_MATRIX,
} from "~/utils/three";

const DEBUG_CHUNKS: boolean = false; // TODO: control via ui toggle

export enum RENDER_MODE {
  FLAT,
  ORTHO,
}

export type ThreeRendererConfig = {
  pixelRatio?: number;
  antialias?: boolean;
  backgroundColor?: string;
  renderMode?: RENDER_MODE;
};

type ChunkRenderData = {
  chunk: VoxelChunk;
  mesh: THREE.InstancedMesh<THREE.BufferGeometry, THREE.NodeMaterial>;
  terrainTypeBuffer: THREE.InstancedBufferAttribute;
  outlineMesh?: THREE.LineSegments;
};

export class ThreeRenderer {
  // THREE.js objects
  private renderer: THREE.WebGPURenderer;
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera | THREE.PerspectiveCamera;

  // Size settings
  private width: number;
  private height: number;
  private FRUSTUM_SIZE = 500;
  private renderMode: RENDER_MODE;
  private zoomLevel: number = 0.0; // Track zoom level (1 = no zoom, >1 = zoomed in, <1 = zoomed out)

  // Objects cache
  private chunkRenderMap: Map<number, ChunkRenderData> = new Map();

  // Terrain-following background
  private terrainBackground: TerrainBackground;

  /**
   * Create a new Three.js renderer
   */
  constructor(
    private container: HTMLElement,
    config: ThreeRendererConfig,
  ) {
    this.width = container.clientWidth;
    this.height = container.clientHeight;
    if (!(this.width >= 300 && this.height >= 300)) {
      throw new Error(
        `Canvas width and height are too small: ${this.width}x${this.height}`,
      );
    }

    this.renderMode = config.renderMode ?? RENDER_MODE.ORTHO;

    // Create renderer
    this.renderer = new THREE.WebGPURenderer({
      antialias: config.antialias ?? true,
    });
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(config.pixelRatio ?? window.devicePixelRatio);
    this.container.appendChild(this.renderer.domElement);

    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(
      config.backgroundColor || "#87CEEB",
    );

    this.camera = this.initCamera();

    this.listenForResize();

    // Initialize camera position
    // this.updateCamera(playerPosition);

    // Add lights
    this.setupLights();

    // Initialize terrain background
    this.terrainBackground = new TerrainBackground(this.scene);
    this.terrainBackground.update();
  }

  public registerEntity(entity: THREE.Object3D): void {
    this.scene.add(entity);
  }

  public getRenderer() {
    return this.renderer;
  }

  private onRenderCallbacks: ((renderer: THREE.WebGPURenderer) => void)[] = [];
  public onRender(fn: (renderer: THREE.WebGPURenderer) => void): {
    unregister: () => void;
  } {
    this.onRenderCallbacks.push(fn);
    return {
      unregister: () => {
        const index = this.onRenderCallbacks.indexOf(fn);
        if (index !== -1) {
          this.onRenderCallbacks.splice(index, 1);
        }
      },
    };
  }

  private initCamera(): THREE.OrthographicCamera | THREE.PerspectiveCamera {
    // Create camera
    const far = 6000;
    if (this.renderMode === RENDER_MODE.FLAT) {
      this.camera = new THREE.OrthographicCamera();
      this.camera.near = -1000;
      this.camera.far = far;
    } else {
      this.camera = new THREE.PerspectiveCamera();
      this.camera.near = 0.1;
      this.camera.far = far;
    }

    this.updateCamera();

    return this.camera;
  }

  // public cameraFrustumContainsPoint(point: THREE.Vector3): boolean {
  //   const camera = this.camera;
  //   camera.updateMatrixWorld();
  //   camera.updateProjectionMatrix();

  //   const frustum = new THREE.Frustum();
  //   frustum.setFromProjectionMatrix(
  //     new THREE.Matrix4().multiplyMatrices(
  //       camera.projectionMatrix,
  //       camera.matrixWorldInverse,
  //     ),
  //   );

  //   return frustum.containsPoint(point);
  // }

  /**
   * Set up lights for the scene
   */
  private setupLights(): void {
    // Ambient light for base illumination - reduced to prevent over-lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    ambientLight.castShadow = false;
    this.scene.add(ambientLight);

    // Main forward directional light - reduced intensity
    const sideLight = new THREE.DirectionalLight(0xffffff, 0.7);
    sideLight.position.set(0, -10, 100);
    // sideLight.target.position.set(0, 0, 0);
    sideLight.castShadow = false;
    this.scene.add(sideLight);

    // Fill light from opposite direction to eliminate dark areas
    const fillLight = new THREE.DirectionalLight(0xffffff, 1);
    fillLight.position.set(-100, 50, -50);
    fillLight.castShadow = false;
    this.scene.add(fillLight);

    this.mainLights.push(fillLight, sideLight, ambientLight);
  }

  public mainLights: THREE.Light[] = [];

  private frameTimestampHistory: number[] = Array.from<number>({
    length: 100,
  }).fill(0);
  private frameTimestampHistoryIndex: number = 0;

  public startRenderLoop(): void {
    void this.renderer.setAnimationLoop(
      (timestamp: DOMHighResTimeStamp, _frame?: XRFrame) => {
        this.frameTimestampHistory[this.frameTimestampHistoryIndex] = timestamp;
        this.frameTimestampHistoryIndex =
          (this.frameTimestampHistoryIndex + 1) %
          this.frameTimestampHistory.length;

        for (const fn of this.onRenderCallbacks) fn(this.renderer);

        this.renderer.render(this.scene, this.camera);
      },
    );
  }

  public averageFPS(): number {
    // If the array is not full yet, we only want to consider non-zero values
    const validFrames = this.frameTimestampHistory.filter((t) => t > 0);

    if (validFrames.length <= 1) {
      return 0;
    }

    // Calculate the time differences between consecutive frames
    const frameTimes: number[] = [];

    // Sort the timestamps to ensure they're in chronological order
    const sortedFrames = [...validFrames].sort((a, b) => a - b);

    // Calculate time differences
    for (let i = 1; i < sortedFrames.length; i++) {
      const delta = sortedFrames[i]! - sortedFrames[i - 1]!;
      if (delta > 0) {
        // Avoid division by zero
        frameTimes.push(delta);
      }
    }

    if (frameTimes.length === 0) {
      return 0;
    }

    // Calculate average frame time in milliseconds
    const averageFrameTime =
      frameTimes.reduce((sum, time) => sum + time, 0) / frameTimes.length;

    // Convert to FPS (1000ms / frameTime)
    return 1000.0 / averageFrameTime;
  }

  private terrainVoxelGeom?: THREE.BufferGeometry;

  /**
   * Update terrain chunk from VoxelChunk
   */
  public updateVoxelChunk(chunk: VoxelChunk): void {
    let chunkRenderData = this.chunkRenderMap.get(chunk.id);
    if (!chunkRenderData) {
      chunkRenderData = this.createChunkMesh(chunk);
      this.chunkRenderMap.set(chunk.id, chunkRenderData);
    }

    const dirtyPhysicsRect = chunk.needsRenderRect;
    if (!dirtyPhysicsRect.dirty) return;

    const { voxelSize, chunkWidthInVoxels } = GameEngine.voxelTerrain;

    for (
      let localX = dirtyPhysicsRect.minX;
      localX <= dirtyPhysicsRect.maxX;
      localX++
    ) {
      for (
        let localY = dirtyPhysicsRect.minY;
        localY <= dirtyPhysicsRect.maxY;
        localY++
      ) {
        const index = localX + localY * chunkWidthInVoxels;

        const voxelStruct = chunk.voxelAt(index);

        if (!voxelStruct.modified) continue;
        voxelStruct.modified = false;

        const terrainType = voxelStruct.terrainType;

        if (terrainType === VOXEL_EMPTY) {
          chunkRenderData.mesh.setMatrixAt(index, ZEROED_MATRIX);
          chunkRenderData.mesh.instanceMatrix.needsUpdate = true;
        } else {
          const destruction = voxelStruct.destruction;
          const voxelUid = voxelStruct.uid;

          const terrainInfo = TERRAIN_TYPES_MAP[terrainType]!;

          tmpObject.position.set(
            localX * voxelSize + voxelSize * 0.5,
            localY * voxelSize + voxelSize * 0.5,
            this.deterministicZOffset(voxelUid, terrainInfo),
          );

          const scale =
            destruction <= 0 ? 1 : mapNumber(destruction, 0, 1, 1, 0.05);

          tmpObject.scale.set(scale, scale, terrainInfo.zLength);

          tmpObject.updateMatrix();

          chunkRenderData.mesh.setMatrixAt(index, tmpObject.matrix);
          chunkRenderData.mesh.instanceMatrix.needsUpdate = true;

          chunkRenderData.mesh.setColorAt(
            index,
            this.computeVoxelColor(voxelUid, terrainInfo),
          );
          if (!chunkRenderData.mesh.instanceColor)
            throw new Error("No instance color");
          chunkRenderData.mesh.instanceColor.needsUpdate = true;
        }
        chunkRenderData.terrainTypeBuffer.array[index] = terrainType;
        chunkRenderData.terrainTypeBuffer.needsUpdate = true;
      }
    }

    chunk.needsRenderRect.reset();
  }

  private deterministicZOffset(
    voxelUid: number,
    terrainInfo: TerrainType,
  ): number {
    if (this.renderMode === RENDER_MODE.FLAT) return 0;

    const seed = 1853;

    return mapNumber(
      pseudoRandom(voxelUid + seed),
      0.0,
      1.0,
      -terrainInfo.zLength * 0.25,
      terrainInfo.zLength * 0.25,
    );
  }
  private computeVoxelColor(voxelUid: number, terrainInfo: TerrainType) {
    const seed = 4103;

    const skewScale = terrainInfo.opacity != null ? 0.18 : 0.04;

    const colorSkew = mapNumber(
      pseudoRandom(voxelUid + seed),
      0.0,
      1.0,
      -skewScale,
      skewScale,
    );

    return tmpColor.set(terrainInfo.color).offsetHSL(0, 0, colorSkew);
  }

  private hoveredVoxels: Voxel[] = [];
  public setHoveredVoxels(voxels: Voxel[]): void {
    // clear previous hovered voxels
    for (const prevHoveredVoxel of this.hoveredVoxels) {
      const chunkRenderData = this.chunkRenderMap.get(
        prevHoveredVoxel.chunk.id,
      );
      if (!chunkRenderData) continue;

      const indexInChunk = prevHoveredVoxel.indexInChunk;

      chunkRenderData.mesh.setColorAt(
        indexInChunk,
        this.computeVoxelColor(
          prevHoveredVoxel.uid,
          prevHoveredVoxel.terrainInfo,
        ),
      );
      if (!chunkRenderData.mesh.instanceColor)
        throw new Error("No instance color");
      chunkRenderData.mesh.instanceColor.needsUpdate = true;
    }

    // set new hovered voxels
    for (const voxel of voxels) {
      const chunkRenderData = this.chunkRenderMap.get(voxel.chunk.id);
      if (!chunkRenderData) continue;

      const indexInChunk = voxel.indexInChunk;

      const { opacity } = voxel.terrainInfo;

      const color = this.computeVoxelColor(
        voxel.uid,
        voxel.terrainInfo,
      ).offsetHSL(0, 0, 0.1 * (opacity ?? 1.0));

      chunkRenderData.mesh.setColorAt(indexInChunk, color);
      if (!chunkRenderData.mesh.instanceColor)
        throw new Error("No instance color");
      chunkRenderData.mesh.instanceColor.needsUpdate = true;
    }

    this.hoveredVoxels = voxels;
  }

  private createChunkMesh(chunk: VoxelChunk): ChunkRenderData {
    if (!chunk.voxelStructs) throw new Error("Chunk has no voxelStructs");

    const {
      voxelSize,
      voxelsPerChunk: voxelCount,
      chunkWidth,
      chunkHeight,
      chunkWidthInVoxels,
    } = GameEngine.voxelTerrain;

    // --- setup geometry

    // Create a single instance of cube geometry to reuse
    let geometry;
    if (this.renderMode === RENDER_MODE.FLAT) {
      geometry = new THREE.PlaneGeometry(voxelSize, voxelSize);
    } else {
      geometry = new THREE.BoxGeometry(voxelSize, voxelSize, 1);
    }

    // technically we could compute all other data (including position and color) from this buffer...
    const terrainTypeBuffer = new THREE.InstancedBufferAttribute(
      // we can directly use the buffer from the voxel data!
      chunk.voxelStructs.buffers.terrainType,
      1,
    );

    geometry.setAttribute("instanceTerrainType", terrainTypeBuffer);

    // --- setup material

    const material = new THREE.MeshStandardNodeMaterial({});

    material.transparent = true;
    // material.alphaHash = true;
    // material.forceSinglePass = true;

    // disable depth writing manually (optional fallback)
    // material.depthWrite = false;

    // for transparent voxels, set all their depths to 0 so they render first.
    // Then for non-transparent voxels, keep the default depth behavior.
    // material.depthNode = Fn(() => {
    //   const terrainTypeAttr = attribute("instanceTerrainType", "uint");
    //   const depthVar = float().toVar();

    //   Switch(terrainTypeAttr)
    //     .Case(
    //       ...opacityMapping.map(([terrainId, _opacity]) => terrainId),
    //       tslCaseBlock(() => {
    //         depthVar.assign(
    //           defaultDepth.add(positionView.z.negate().sign().mul(0.00001)),
    //         );
    //       }),
    //     )
    //     .Default(() => {
    //       depthVar.assign(defaultDepth);
    //     });

    //   return depthVar;
    // })();

    // Create TSL switch statement for opacity based on terrainType
    material.opacityNode = Fn(() => {
      const terrainTypeAttr = attribute("instanceTerrainType", "uint");

      const opacityVar = float().toVar();

      // Build the switch statement dynamically from `opacityMapping`
      TERRAIN_TYPE_LIST.filter((t) => t.opacity != null)
        .map((t) => [t.id, t.opacity!] as const)
        .reduce(
          (acc, [terrainId, opacity]) =>
            acc.Case(uint(terrainId), () => {
              opacityVar.assign(float(opacity));
            }),
          Switch(terrainTypeAttr),
        )
        .Default(() => {
          opacityVar.assign(float(1.0));
        });

      return opacityVar;
    })();

    material.roughnessNode = Fn(() => {
      const terrainTypeAttr = attribute("instanceTerrainType", "uint");

      const roughnessVar = float().toVar();

      TERRAIN_TYPE_LIST.reduce(
        (acc, terrainInfo) =>
          acc.Case(uint(terrainInfo.id), () => {
            roughnessVar.assign(float(terrainInfo.roughness));
          }),
        Switch(terrainTypeAttr),
      );

      return roughnessVar;
    })();

    material.metalnessNode = Fn(() => {
      const terrainTypeAttr = attribute("instanceTerrainType", "uint");

      const metalnessVar = float().toVar();

      TERRAIN_TYPE_LIST.reduce(
        (acc, terrainInfo) =>
          acc.Case(uint(terrainInfo.id), () => {
            metalnessVar.assign(float(terrainInfo.metalness));
          }),
        Switch(terrainTypeAttr),
      );

      return metalnessVar;
    })();

    // --- setup mesh

    const mesh = new THREE.InstancedMesh(geometry, material, voxelCount);

    mesh.position.set(
      chunk.chunkPlacement.x * chunkWidth,
      chunk.chunkPlacement.y * chunkHeight,
      0,
    );

    // Add debug outline if enabled
    let outlineMesh: THREE.LineSegments | undefined;
    if (DEBUG_CHUNKS) {
      outlineMesh = new THREE.LineSegments(
        new THREE.BufferGeometry().setFromPoints(
          outlineDebugRectPoints(0, 0, chunkWidth, chunkHeight),
        ),
        new THREE.LineBasicMaterial({
          color: 0xff69b4, // Bright pink
          linewidth: 1,
        }),
      );
      outlineMesh.position.copy(mesh.position);
      this.scene.add(outlineMesh);
    }

    // Create meshes for each voxel
    for (let index = 0; index < voxelCount; index++) {
      const voxelStruct = chunk.voxelAt(index);

      // Get terrain-type
      const terrainType = voxelStruct.terrainType;

      // terrainTypeBuffer.array[index] = terrainType;

      if (terrainType === VOXEL_EMPTY) {
        // a hack to make sure the voxel is not rendered
        mesh.setMatrixAt(index, ZEROED_MATRIX);
        continue;
      }

      const voxelUid = voxelStruct.uid;

      const terrainInfo = TERRAIN_TYPES_MAP[terrainType]!;

      const localX = index % chunkWidthInVoxels;
      const localY = Math.floor(index / chunkWidthInVoxels);

      // Position within chunk - for side view, y becomes negative z (going down)
      // Voxel coordinates represent top-left corner, but Three.js cubes render at their center
      // So we need to offset by +half voxel size to center the cube within the voxel area
      tmpObject.position.set(
        localX * voxelSize + voxelSize * 0.5,
        localY * voxelSize + voxelSize * 0.5,
        this.deterministicZOffset(voxelUid, terrainInfo),
      );
      tmpObject.scale.set(1, 1, terrainInfo.zLength);
      tmpObject.updateMatrix();

      mesh.setMatrixAt(index, tmpObject.matrix);

      const color = this.computeVoxelColor(voxelUid, terrainInfo);
      mesh.setColorAt(index, color);
    }

    // make sure we set `setColorAt` at least once
    if (!mesh.instanceColor) {
      mesh.setColorAt(0, tmpColor.set(0, 0, 0));
    }

    // setting bounding box/sphere so the chunk shows up properly when frustum-culling.
    // THREE.js seems to be auto-computing the `boundingSphere` incorrectly, so we need to set it manually.
    // Also setting the `boundingBox` for future-proofing.
    const dx = 0; // bounding box/sphere appear to be relative to the mesh's position
    const dy = 0;
    mesh.boundingBox = new THREE.Box3(
      new THREE.Vector3(dx - chunkWidth * 0.5, dy - chunkHeight * 0.5, 0),
      new THREE.Vector3(dx + chunkWidth * 0.5, dy + chunkHeight * 0.5, 1),
    );
    mesh.boundingSphere = new THREE.Sphere(
      new THREE.Vector3(chunkWidth / 2, chunkWidth / 2, 0),
      chunkWidth / 2,
    );

    this.scene.add(mesh);

    // mark as dirty

    mesh.instanceMatrix.needsUpdate = true;
    if (!mesh.instanceColor) throw new Error("No instance color");
    mesh.instanceColor.needsUpdate = true;

    return {
      chunk,
      mesh,
      terrainTypeBuffer,
      outlineMesh,
    };
  }

  public setVoxelDestruction(
    chunkId: number,
    indexInChunk: number,
    /** [0,1) where 0 is no destruction and 1 is fully destroyed */
    destruction: number,
  ): void {
    const chunkRenderData = this.chunkRenderMap.get(chunkId);
    if (!chunkRenderData) return;

    const scale = destruction <= 0 ? 1 : mapNumber(destruction, 0, 1, 1, 0.05);

    const mesh = chunkRenderData.mesh;
    mesh.getMatrixAt(indexInChunk, tmpObject.matrix);
    tmpObject.position.setFromMatrixPosition(tmpObject.matrix);
    tmpObject.scale.setFromMatrixScale(tmpObject.matrix);

    tmpObject.scale.x = scale;
    tmpObject.scale.y = scale;

    tmpObject.updateMatrix();
    mesh.setMatrixAt(indexInChunk, tmpObject.matrix);
    mesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * Update the camera position to follow the player
   */
  public updateCameraPosition(position: Vector2): void {
    this.camera.position.x = position.x;
    this.camera.position.y = position.y;

    // this.camera.lookAt(position.x, position.y, 0);
    // this.camera.up.set(0, 1, 0);
  }

  public zoomCameraBy(
    /** negative delta zooms in, positive delta zooms out. probably -8 or 8. */
    delta: number,
  ): void {
    this.zoomLevel += delta;
    this.updateCamera();
  }

  private updateCamera(): void {
    const aspectRatio = this.width / this.height;

    if (this.camera instanceof THREE.OrthographicCamera) {
      const baseHeight = this.FRUSTUM_SIZE;
      const baseWidth = baseHeight * aspectRatio;

      // Scale width/height uniformly to preserve aspect ratio
      // zoomLevel of 0 => scale 1. Negative zoomLevel => zoom in (smaller frustum)
      const zoomScale = Math.max(0.05, 1 + this.zoomLevel / this.FRUSTUM_SIZE);

      const halfWidth = (baseWidth * zoomScale) / 2;
      const halfHeight = (baseHeight * zoomScale) / 2;

      this.camera.left = -halfWidth;
      this.camera.right = halfWidth;
      this.camera.top = halfHeight;
      this.camera.bottom = -halfHeight;
      this.camera.updateProjectionMatrix();
    } else {
      this.camera.position.z = 600 + this.zoomLevel;

      if (this.camera.aspect !== aspectRatio) {
        this.camera.aspect = aspectRatio;
        this.camera.updateProjectionMatrix();
      }
    }
  }

  public screenToWorldPosition(screenPosition: Vector2): Vector2 {
    const camera = this.camera;
    const vec = tmpVector3;

    // Convert screen (pixel) coordinates to normalized device coordinates (-1 to +1)
    vec.set(
      (screenPosition.x / this.width) * 2 - 1,
      -(screenPosition.y / this.height) * 2 + 1,
      0,
    );

    if (camera instanceof THREE.OrthographicCamera) {
      // For orthographic, unproject at z=0 (any z works, it's a flat projection)
      vec.unproject(camera);
      return vec;
    } else {
      // For perspective, unproject at z=0.5 (NDC z=0.5 is the near plane)
      vec.z = 0.5;
      vec.unproject(camera);
      // Ray from camera to unprojected point
      vec.sub(camera.position).normalize();
      // Find intersection with z=0 plane (assuming world z=0 is the ground)
      const t = -camera.position.z / vec.z;
      const worldPos = vec.multiplyScalar(t).add(camera.position);
      return worldPos;
    }
  }

  private unsubscribers: (() => void)[] = [];

  private listenForResize(): void {
    // Handle window resize
    const handleResize = debounce(() => {
      this.resize();
    }, 100);

    window.addEventListener("resize", handleResize);

    this.unsubscribers.push(() => {
      handleResize.cancel();
      window.removeEventListener("resize", handleResize);
    });
  }

  private resize(): void {
    this.width = this.container.clientWidth;
    this.height = this.container.clientHeight;

    // Update camera
    this.updateCamera();

    // Update renderer
    this.renderer.setSize(this.width, this.height);
  }

  public loadedChunkCount(): number {
    return this.chunkRenderMap.size;
  }

  /**
   * Update visible terrain chunks
   */
  public async updateVisibleTerrain(fromPosition: Vector2) {
    let halfWidth: number;
    let halfHeight: number;
    if (this.camera instanceof THREE.OrthographicCamera) {
      // For orthographic camera, use the exact frustum dimensions
      const camera = this.camera;
      halfWidth = camera.right; // camera.right is positive, camera.left is negative
      halfHeight = camera.top; // camera.top is positive, camera.bottom is negative
    } else {
      // For perspective camera, calculate exact visible area at z=0
      const camera = this.camera;

      // Calculate the frustum dimensions at the camera's z position
      const distance = Math.abs(camera.position.z);
      const DEG2RAD = Math.PI / 180.0;
      const halfFov = camera.fov * DEG2RAD * 0.5;
      halfHeight = distance * Math.tan(halfFov);
      halfWidth = halfHeight * camera.aspect;
    }

    const margin = GameEngine.voxelTerrain.chunkWidth * 0.25;

    // Calculate the visible area bounds
    const minX = fromPosition.x - halfWidth - margin;
    const maxX = fromPosition.x + halfWidth + margin;
    const minY = fromPosition.y - halfHeight - margin;
    const maxY = fromPosition.y + halfHeight + margin;

    const chunksInView = await GameEngine.voxelTerrain.loadChunksInRect(
      { x: minX, y: minY },
      { x: maxX, y: maxY },
    );

    // Get chunks in this area

    let terrainWasModified = false;

    // Remove chunks that are no longer in view
    const visibleChunkIds = new Set<number>();

    for (const chunk of chunksInView) {
      visibleChunkIds.add(chunk.id);

      // Update each modified chunk
      if (chunk.needsRenderRect.dirty || !this.chunkRenderMap.has(chunk.id)) {
        // If any chunk was modified or is spawning in, we need to update the background
        terrainWasModified = true;

        this.updateVoxelChunk(chunk);
      }
    }

    // Update terrain-following background if terrain was modified
    if (terrainWasModified) {
      this.terrainBackground.update();
    }

    this.chunkRenderMap.forEach((chunkRenderData, id) => {
      if (!visibleChunkIds.has(id)) {
        disposeMesh(chunkRenderData.mesh);
        if (chunkRenderData.outlineMesh) {
          disposeMesh(chunkRenderData.outlineMesh);
        }
        this.chunkRenderMap.delete(id);
      }
    });

    // Clear modified flags after rendering
    // GameEngine.voxelTerrain.clearModifiedFlags();
  }

  public updateRenderMode(newRenderMode: RENDER_MODE): void {
    if (this.renderMode === newRenderMode) return;

    this.renderMode = newRenderMode;

    this.camera = this.initCamera();

    const playerPos = GameEngine.player?.position;

    // Update camera position to current player position if available
    if (playerPos) {
      this.updateCameraPosition(playerPos);
    }

    // Clear and regenerate all chunks to use new geometry
    this.clearAllChunks();
    if (playerPos) {
      void this.updateVisibleTerrain(playerPos);
    }
  }

  private clearAllChunks(): void {
    for (const chunkData of this.chunkRenderMap.values()) {
      disposeMesh(chunkData.mesh);

      if (chunkData.outlineMesh) {
        disposeMesh(chunkData.outlineMesh);
      }
    }
    this.chunkRenderMap.clear();
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    this.terrainVoxelGeom?.dispose();

    // Dispose of background
    this.terrainBackground.dispose();

    this.clearAllChunks();

    // Dispose of all meshes
    for (const obj of this.scene.children) {
      disposeMesh(obj);
    }

    // Remove renderer from DOM
    if (this.container.contains(this.renderer.domElement)) {
      this.container.removeChild(this.renderer.domElement);
    }

    this.renderer.dispose();

    for (const unsubscribe of this.unsubscribers) unsubscribe();
    this.unsubscribers.length = 0;
  }
}

function outlineDebugRectPoints(
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
) {
  return [
    // Bottom face
    new THREE.Vector3(minX, minY, 0),
    new THREE.Vector3(maxX, minY, 0),
    new THREE.Vector3(maxX, minY, 0),
    new THREE.Vector3(maxX, maxY, 0),
    new THREE.Vector3(maxX, maxY, 0),
    new THREE.Vector3(minX, maxY, 0),
    new THREE.Vector3(minX, maxY, 0),
    new THREE.Vector3(minX, minY, 0),
    // Diagonal lines
    new THREE.Vector3(minX, minY, 0),
    new THREE.Vector3(maxX, maxY, 0),
    new THREE.Vector3(minX, maxY, 0),
    new THREE.Vector3(maxX, minY, 0),
  ];
}
